import type { Theme } from "@mui/material";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormLabel,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import * as React from "react";
import { DIALOG_WIDTH } from "../../constants";
import type { AlertSchema } from "../../types";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => {
  return (
    <Stack spacing={0.6}>
      <FormLabel>{label}</FormLabel>
      <Box>{value}</Box>
    </Stack>
  );
};

const AlertSchemaDetailsDialog: React.FC<{
  alertSchema: AlertSchema | null;
  onClose: () => void;
}> = ({ alertSchema, onClose }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  return (
    <Dialog
      open={Boolean(alertSchema)}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.md },
      }}
    >
      <StandardDialogTitle
        title={alertSchema?.name ?? "Alert schema details"}
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <DialogContent sx={{ overflowX: "hidden" }}>
        {!alertSchema ? null : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Alert schema details
            </Typography>

            <Divider />

            <DetailRow
              label="Description"
              value={
                <Typography variant="body2">
                  {alertSchema.description?.trim() || "No description"}
                </Typography>
              }
            />

            <DetailRow
              label="Status property"
              value={<Typography variant="body2">{alertSchema.statusProp}</Typography>}
            />

            <DetailRow
              label="Foreign keys"
              value={
                <Stack direction="row" flexWrap="wrap" gap={0.8}>
                  {alertSchema.foreignKeyProps.map((foreignKeyProp) => (
                    <Chip
                      key={foreignKeyProp}
                      label={foreignKeyProp}
                      size="small"
                    />
                  ))}
                </Stack>
              }
            />

            <DetailRow
              label="Status mappings"
              value={
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Critical: {alertSchema.statusMappingCritical ?? "-"}
                  </Typography>
                  <Typography variant="body2">
                    Warning: {alertSchema.statusMappingWarning ?? "-"}
                  </Typography>
                  <Typography variant="body2">
                    Resolved: {alertSchema.statusMappingResolved ?? "-"}
                  </Typography>
                </Stack>
              }
            />

            <DetailRow
              label="Type"
              value={
                <Typography variant="body2">
                  {alertSchema.system ? "System" : "Custom"}
                </Typography>
              }
            />
          </Stack>
        )}
      </DialogContent>

      <StandardDialogActions
        primaryText="Close"
        cancelText=""
        canCancel={false}
        onPrimaryClicked={onClose}
        onCancel={onClose}
        loading={false}
      />
    </Dialog>
  );
};

export default AlertSchemaDetailsDialog;
